import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, X, Maximize2 } from 'lucide-react';

interface Artwork {
  _id: string;
  title: string;
  description?: string;
  filename?: string;
  imageUrl?: string;    // ADD
  mimetype?: string;
  views?: number;
  createdAt?: string;
}

const GalleryGrid: React.FC = () => {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<Artwork | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const API_BASE = "https://art-gallery-backend-fabx.onrender.com";
  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/gallery`);
      setArtworks(response.data.artworks || []);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Backend sync AFTER optimistic update
  // const openModal = async (artwork: Artwork) => {
  //   setUpdatingId(artwork._id);

  //   // 1. Optimistic update (instant visual feedback)
  //   const newViews = (artwork.views ?? 0) + 1;
  //   setArtworks(prev => prev.map(item =>
  //     item._id === artwork._id
  //       ? { ...item, views: newViews }
  //       : item
  //   ));

  //   // 2. Backend sync (persistent storage)
  //   try {
  //     await axios.put(`${API_BASE}/api/admin/artworks/${artwork._id}`, {
  //       views: newViews,
  //     });
  //   } catch (error) {
  //     console.error('Failed to increment views:', error);
  //     // ✅ DON'T revert - keep optimistic update
  //   }

  //   // 3. Modal with updated count
  //   setSelectedImage({ ...artwork, views: newViews });
  //   setImageScale(1);
  //   setIsLoadingImage(true);
  //   document.body.style.overflow = 'hidden';

  //   setUpdatingId(null);
  // };

  // const closeModal = () => {
  //   setSelectedImage(null);
  //   setImageScale(1);
  //   document.body.style.overflow = 'unset';
  //   // ✅ NO reset - counter stays increased!
  // };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setImageScale(prev => Math.max(0.5, Math.min(3, prev * delta)));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center py-20 px-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-facebook-500/20 border-t-facebook-500 rounded-full animate-spin mx-auto mb-6" />
          <p className="text-lg text-fgray-400">Loading your feed...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-2 sm:px-4 lg:px-8 py-4 sm:py-6 space-y-3 sm:space-y-4 min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-5 xl:gap-6 max-w-7xl mx-auto">
          {artworks.map((artwork) => (
            <div
              key={artwork._id}
              className="group relative bg-darkbg-800/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 cursor-pointer border border-fgray-800/50 hover:border-facebook-500/20 h-full"
            >
              <div
                className="w-full overflow-hidden rounded-t-2xl aspect-[4/5] sm:aspect-[3/4]"
                // onClick={() => openModal(artwork)}
              >
                <img
                  src={artwork.imageUrl}  // ✅ Cloudinary URL
                  alt={artwork.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 hover:brightness-[1.05]"
                  loading="lazy"
                />
              </div>

              {/* ✅ COUNTER INCREASES EVERY TIME */}
              <div className="px-4 py-2.5 bg-darkbg-800/95 backdrop-blur-sm border-t border-fgray-700/50">
                <div className="flex items-center justify-between text-xs">
                  <h3 className="font-semibold text-white line-clamp-1 flex-1 mr-3 pr-1">
                    {artwork.title}
                  </h3>

                  <div className={`flex items-center gap-1 px-2 py-1 bg-fgray-800/50 backdrop-blur-sm rounded-lg border border-fgray-600/30 text-fgray-300 font-medium whitespace-nowrap transition-all duration-200 hover:scale-105 ${updatingId === artwork._id
                      ? 'bg-facebook-500/40 border-facebook-400/60 shadow-md scale-105 animate-pulse ring-2 ring-facebook-400/50'
                      : ''
                    }`}>
                    <Eye className={`w-3.5 h-3.5 transition-all duration-200 ${updatingId === artwork._id
                        ? 'text-facebook-400 animate-ping'
                        : 'text-facebook-400/80 hover:text-facebook-400'
                      }`} />
                    <span className="font-mono font-bold">{artwork.views?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal - FULLY WORKING */}
      {selectedImage && (
        <>
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-8 animate-in fade-in zoom-in duration-300"
            // onClick={closeModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8">
            <div className="relative max-w-6xl max-h-[95vh] w-full h-full flex items-center justify-center animate-in fade-in zoom-in duration-300">
              <button
                // onClick={closeModal}
                className="absolute -top-4 right-4 w-12 h-12 bg-black/70 hover:bg-black/90 rounded-2xl flex items-center justify-center text-white transition-all z-10 backdrop-blur-sm shadow-2xl hover:scale-110"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="absolute top-6 left-6 flex gap-3 bg-black/60 backdrop-blur-sm rounded-2xl p-3 z-10">
                <button
                  // onClick={closeModal}
                  className="w-12 h-12 rounded-xl bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all group hover:scale-110"
                  title="Exit fullscreen (ESC)"
                >
                  <Maximize2 className="w-6 h-6 group-hover:rotate-180 transition-transform duration-200" />
                </button>
              </div>

              {isLoadingImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-3xl">
                  <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-2xl animate-spin" />
                </div>
              )}

              <img
                src={`${API_BASE}/images/${selectedImage.filename}`}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl cursor-grab active:cursor-grabbing select-none"
                style={{ transform: `scale(${imageScale})` }}
                onWheel={handleWheel}
                onLoad={() => setIsLoadingImage(false)}
                onError={() => setIsLoadingImage(false)}
              />

              <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-xl rounded-2xl px-6 py-3 text-white max-w-4xl mx-auto border border-fgray-700/30 shadow-2xl">
                <div className="flex items-center justify-between text-sm">
                  <h2 className="font-bold text-white truncate flex-1 mr-4">
                    {selectedImage.title}
                  </h2>

                  <div className="flex items-center gap-2 px-3 py-1.5 bg-fgray-800/60 backdrop-blur-sm rounded-xl border border-fgray-600/40 text-fgray-200 font-semibold">
                    <Eye className="w-4 h-4 text-facebook-400" />
                    <span className="font-mono font-bold">{selectedImage.views?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {artworks.length === 0 && !loading && (
        <div className="text-center py-32 px-8">
          <div className="text-8xl opacity-20 mb-12 mx-auto">🖼️</div>
          <h2 className="text-4xl sm:text-5xl font-black text-fgray-400 mb-6">No artworks yet</h2>
          <p className="text-xl sm:text-2xl text-fgray-500 max-w-lg mx-auto leading-relaxed">
            Check back later for amazing digital art from our creators!
          </p>
        </div>
      )}
    </>
  );
};

export default GalleryGrid;
